import React, { useState, useRef, useEffect } from "react";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sparkles, Send, X, Bot, User as UserIcon, Loader2 } from "lucide-react";

const PROMPTS = [
  "Why is my COLA positive or negative?",
  "Explain hypothetical tax vs actual host tax",
  "How is my saving capacity calculated?",
  "What housing support do I get and why?",
];

export function AIAssistant({ calculationId }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open && calculationId) {
      fetch(`${API}/assistant/history/${calculationId}`, { credentials: "include" })
        .then((r) => r.json()).then((d) => setMessages(d.messages || [])).catch(() => {});
    }
  }, [open, calculationId]);

  useEffect(() => { scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight); }, [messages]);

  const send = async (q) => {
    const question = q || input;
    if (!question.trim() || streaming || !calculationId) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: question }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const resp = await fetch(`${API}/assistant/chat`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ calculation_id: calculationId, question }),
      });
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: acc }; return c; });
      }
    } catch (e) {
      setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: "Sorry, I could not reach the assistant. Please try again." }; return c; });
    } finally { setStreaming(false); }
  };

  if (!calculationId) return null;

  return (
    <>
      <button
        data-testid="ai-assistant-fab"
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-full bg-essred hover:bg-essred-700 text-white px-5 py-3.5 exec-shadow transition-all hover:scale-105"
      >
        <Sparkles className="h-5 w-5" /> <span className="font-semibold text-sm hidden sm:inline">Ask Assistant</span>
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="ai-assistant-drawer">
          <div className="absolute inset-0 bg-navy/40 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="relative w-full max-w-md bg-white h-full flex flex-col exec-shadow animate-fade-up">
            <div className="brand-gradient text-white px-5 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="h-9 w-9 rounded-lg bg-white/15 flex items-center justify-center"><Bot className="h-5 w-5" /></div>
                <div>
                  <div className="font-display font-bold leading-none">Mobility Assistant</div>
                  <div className="text-[11px] text-gold mt-0.5">Cites your calculation & sources</div>
                </div>
              </div>
              <button data-testid="ai-close-btn" onClick={() => setOpen(false)}><X className="h-5 w-5" /></button>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.length === 0 && (
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">Ask me anything about your assignment calculation. I answer only from your approved figures and cite sources.</p>
                  {PROMPTS.map((p) => (
                    <button key={p} data-testid="ai-prompt-card" onClick={() => send(p)}
                      className="w-full text-left text-sm rounded-lg border px-3 py-2.5 hover:border-[#F5A623] hover:bg-slate-100 transition-colors">
                      {p}
                    </button>
                  ))}
                </div>
              )}
              {messages.map((m, i) => (
                <div key={i} className={`flex gap-2.5 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
                  <div className={`h-7 w-7 rounded-full flex items-center justify-center shrink-0 ${m.role === "user" ? "bg-slate-50 text-slate-400" : "bg-slate-100 text-slate-900"}`}>
                    {m.role === "user" ? <UserIcon className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
                  </div>
                  <div className={`rounded-2xl px-3.5 py-2.5 text-sm max-w-[80%] whitespace-pre-wrap ${m.role === "user" ? "bg-slate-50 text-slate-400" : "bg-slate-100 text-slate-700"}`}>
                    {m.content || (streaming && i === messages.length - 1 ? <Loader2 className="h-4 w-4 animate-spin" /> : "")}
                  </div>
                </div>
              ))}
            </div>

            <div className="p-3 border-t flex gap-2">
              <Input data-testid="ai-chat-input" value={input} onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Ask about your calculation…" disabled={streaming} />
              <Button data-testid="ai-chat-send-btn" onClick={() => send()} disabled={streaming}
                className="bg-essred hover:bg-essred-700 text-white shrink-0">
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
